import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const EnterpriseDrillContext = createContext(null);

export function EnterpriseDrillProvider({ children }) {
  const location = useLocation();

  const [localPath, setLocalPath] = useState([]);
  const [viewMode, setViewMode] = useState('aggregate');
  const [selectedPortfolioName, setSelectedPortfolioName] = useState(null);
  const [hierarchyKpis, setHierarchyKpis] = useState(null);
  const [hierarchyOrgName, setHierarchyOrgName] = useState(null);
  const [hierarchyTokenEconomy, setHierarchyTokenEconomy] = useState(null);

  const path = localPath;
  const currentLevel = Math.min(path.length, 3);
  const current = path[path.length - 1] || null;
  const entityId = current?.id ?? null;
  const entityName = current?.name ?? null;
  const isPersonLeaf = typeof entityId === 'string' && entityId.includes('@');

  const drillInto = useCallback((entity) => {
    setLocalPath(prev => [...prev, { id: entity.id, name: entity.name, email: entity.email ?? null }]);
  }, []);

  const navigateTo = useCallback((pathIndex) => {
    if (pathIndex < 0) {
      setLocalPath([]);
      setViewMode('aggregate');
      return;
    }
    const trimmed = localPath.slice(0, pathIndex + 1);
    setLocalPath(trimmed);
    setViewMode('aggregate');
  }, [localPath]);

  const resetToTop = useCallback(() => {
    setLocalPath([]);
    setViewMode('aggregate');
  }, []);

  const isEnterpriseRoute = location.pathname === '/enterprise';

  const value = useMemo(() => ({
    currentLevel,
    path,
    entityId,
    entityName,
    isPersonLeaf,
    viewMode,
    setViewMode,
    drillInto,
    navigateTo,
    resetToTop,
    isEnterpriseRoute,
    selectedPortfolioName,
    setSelectedPortfolioName,
    hierarchyKpis,
    setHierarchyKpis,
    hierarchyOrgName,
    setHierarchyOrgName,
    hierarchyTokenEconomy,
    setHierarchyTokenEconomy,
  }), [currentLevel, path, entityId, entityName, isPersonLeaf, viewMode, drillInto, navigateTo, resetToTop, isEnterpriseRoute, selectedPortfolioName, hierarchyKpis, hierarchyOrgName, hierarchyTokenEconomy]);

  return (
    <EnterpriseDrillContext.Provider value={value}>
      {children}
    </EnterpriseDrillContext.Provider>
  );
}

export function useEnterpriseDrillContext() {
  const ctx = useContext(EnterpriseDrillContext);
  if (!ctx) {
    return {
      currentLevel: 0,
      path: [],
      entityId: null,
      entityName: null,
      isPersonLeaf: false,
      viewMode: 'aggregate',
      setViewMode: () => {},
      drillInto: () => {},
      navigateTo: () => {},
      resetToTop: () => {},
      isEnterpriseRoute: false,
      selectedPortfolioName: null,
      setSelectedPortfolioName: () => {},
      hierarchyKpis: null,
      setHierarchyKpis: () => {},
      hierarchyOrgName: null,
      setHierarchyOrgName: () => {},
      hierarchyTokenEconomy: null,
      setHierarchyTokenEconomy: () => {},
    };
  }
  return ctx;
}

export default EnterpriseDrillContext;
